import React, { useState } from 'react';
import {
  Alert, Typography, Paper, Chip, Divider, Stack, Skeleton, TextField, InputAdornment,
  Table, TableHead, TableRow, TableCell, TableBody, ToggleButtonGroup, ToggleButton, Tooltip,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useLocaleState } from 'react-admin';
import __t from '../../../i18n/t';
import { C } from '../../../components/wizardKit/tokens';

// O painel usava um índigo próprio (#667eea), que não é cor da marca nem do cadastro em
// etapas. Agora é o mesmo violeta do resto do wizard. O alternador perdeu o `sx` local:
// quem desenha ToggleButton aqui dentro é o wizardTheme.
const PANEL_SX = { p: 2.5, border: '1px solid rgba(68,51,180,.16)', background: C.violetSoft };
const ACCENT_SX = { color: C.violet };
const TOTAL_CHIP_SX = { bgcolor: '#fff', color: C.violet, border: '1px solid rgba(68,51,180,.16)', fontWeight: 600 };

interface FuelShare { fuelId: string; name: string; share: number; }
interface Bucket { label: string; count: number; }
interface MaxCostLevel { avgLiters: number; discountPerLiter: number; avgTicket: number; }
interface LevelSim {
  levelOrder: number;
  durationDays: number;
  discountPerLiter: number;
  /** Nível de brinde: custo por volta, determinístico. Quando vem, não há desconto a estimar. */
  rewardCost?: number | null;
  // Os 3 campos abaixo só existem em backend novo (contrato aditivo do "Custo
  // máximo"). Ausentes → esconde o toggle e o painel se comporta como antes.
  fuelCoverage?: number;
  coveredFuels?: string[];
  maxCost?: MaxCostLevel;
}
interface Scenario {
  returnRate: number;
  returns: number;
  returnsByLevel: number[];
  estimatedCost: number;
  estimatedRevenue: number;
  // null quando custo é 0 (nenhum nível coberto tem litros/desconto pra dividir).
  revenuePerReal: number | null;
  budgetExhaustedAtReturns: number | null;
}

export interface SimulationResult {
  audience: { total: number; withApp: number; withCellphone: number; byDaysBucket: Bucket[] };
  base: { periodDays: number; fillins: number; avgTicket: number; avgLiters: number; fuelMix: FuelShare[] };
  levels: LevelSim[];
  totalDurationDays: number;
  endsAt: string;
  scenarios: Scenario[];
  // Ausente → backend antigo, sem a opção "Custo máximo".
  scenariosMaxCost?: Scenario[];
}

type SimulationMode = 'mix' | 'max';

interface SimulationPanelProps {
  simulation: SimulationResult | null;
  loading: boolean;
  /** Motivo estrutural (público/níveis incompletos): troca o painel por um aviso. */
  unavailableReason?: string | null;
  /** Falha do simulate com o formulário válido: o painel e o campo "Taxa livre" continuam na tela. */
  error?: string | null;
  customRate: number;
  onCustomRateChange: (rate: number) => void;
  hasBudget: boolean;
}

const money = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Até 3 casas: o desconto por litro é a métrica que essa explicação existe pra mostrar
// (ex.: R$ 0,138), e 2 casas arredondaria pra R$ 0,14. Mínimo 2, pra R$ 0,30 não virar R$ 0,300.
const moneyPerLiter = (v: number) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
const liters = (v: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const pct = (v: number) => `${(v * 100).toFixed(v * 100 % 1 === 0 ? 0 : 1)}%`;
const ratePercentText = (rate: number) => String(Number((rate * 100).toFixed(1)));

// Linhas de shimmer quando ainda não há simulação anterior: 3 taxas fixas + a taxa livre.
const DEFAULT_SKELETON_ROWS = 4;

// Coverage em texto ("46%") e lista de combustíveis em linguagem natural ("A, B e C" /
// "A, B y C") — mesmas regras em ambos os modos do toggle.
const coveragePct = (coverage: number) => `${Math.round(coverage * 100)}%`;

const SimulationPanel: React.FC<SimulationPanelProps> = ({
  simulation, loading, unavailableReason, error, customRate, onCustomRateChange, hasBudget,
}) => {
  // Texto do campo separado da taxa: apagar tudo pra digitar "26" não pode virar "1" (o mínimo) no meio.
  const [rateText, setRateText] = useState(() => ratePercentText(customRate));
  // Estado só local (sem refetch): alterna qual conjunto de cenários a tabela mostra.
  const [mode, setMode] = useState<SimulationMode>('mix');
  const [localeState] = useLocaleState();
  const listLocale = localeState === 'es' ? 'es' : 'pt-BR';
  const fuelListFormatter = new Intl.ListFormat(listLocale, { style: 'long', type: 'conjunction' });

  if (!simulation && !loading && !error) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {unavailableReason || 'Complete o público e os níveis para ver a simulação.'}
        </Typography>
      </Paper>
    );
  }

  const handleRateChange = (text: string) => {
    setRateText(text);
    const value = Number(text);
    // Mínimo 1%: o backend exige taxa > 0 e 0% só zeraria o cenário.
    if (text !== '' && value >= 1 && value <= 100) onCustomRateChange(value / 100);
  };

  // Enquanto recalcula, o painel fica montado e só os números viram shimmer: antes o painel
  // inteiro virava um Skeleton e o campo "Taxa livre" sumia (e perdia o foco) a cada tecla.
  const showData = !loading && simulation;
  const showError = !loading && !simulation && !!error;
  const skeletonRows = simulation?.scenarios.length || DEFAULT_SKELETON_ROWS;
  const skeletonBuckets = simulation?.audience.byDaysBucket.length ?? 0;
  const valueColumns = hasBudget ? 5 : 4;

  // `simulation` continua com o último resultado válido enquanto `loading` recalcula
  // (é o que permite o campo "Taxa livre" nunca sumir), então a capacidade do backend
  // também fica estável — o toggle não pisca entre recálculos.
  const hasMaxCost = !!simulation?.scenariosMaxCost
    && simulation.levels.every((l) => l.rewardCost != null || l.fuelCoverage != null);
  const activeScenarios = (mode === 'max' && hasMaxCost && simulation?.scenariosMaxCost)
    ? simulation.scenariosMaxCost
    : simulation?.scenarios;

  const levelText = (level: LevelSim): string | null => {
    // Nível de brinde: o custo não é estimado, é o preço do pacote. Cobertura de combustível
    // e "custo máximo" não se aplicam — não há desconto por litro para ponderar.
    if (level.rewardCost != null) {
      return __t('Brinde: %{money} por cliente que conquistar, independente do combustível.',
        { money: money(level.rewardCost) });
    }
    // Lista vazia é truthy em JS: sem isso, nível com 0% de cobertura (nenhum combustível
    // vendido tem desconto) cai no fuelListFormatter.format([]) e vira frase quebrada.
    if (level.fuelCoverage == null || !level.coveredFuels || level.coveredFuels.length === 0) {
      return __t('Este nível não tem desconto em nenhum combustível vendido no período.');
    }
    const fuels = fuelListFormatter.format(level.coveredFuels);
    if (mode === 'max') {
      if (!level.maxCost) return null;
      return __t(
        'Supõe que todo cliente que volta abastece %{fuels}: em média %{liters} L por abastecimento e %{money} de desconto por litro.',
        { fuels, liters: liters(level.maxCost.avgLiters), money: moneyPerLiter(level.maxCost.discountPerLiter) },
      );
    }
    return __t(
      'O desconto cobre %{pct} dos litros vendidos na rede (%{fuels}). Nos outros combustíveis não há desconto, então a média fica em %{money} por litro.',
      { pct: coveragePct(level.fuelCoverage), fuels, money: moneyPerLiter(level.discountPerLiter) },
    );
  };

  // Uma linha por nível a partir de 2 níveis (prefixada "Nível N: "); sentenças
  // idênticas (mesmo desconto/cobertura em níveis diferentes) aparecem só uma vez.
  const explanationLines: string[] = [];
  if (hasMaxCost && simulation) {
    const showLevelPrefix = simulation.levels.length >= 2;
    const seen = new Set<string>();
    simulation.levels.forEach((level) => {
      const text = levelText(level);
      if (text == null || seen.has(text)) return;
      seen.add(text);
      explanationLines.push(showLevelPrefix ? `${__t('Nível %{n}:', { n: level.levelOrder })} ${text}` : text);
    });
  }

  return (
    <Paper
      variant="outlined"
      sx={PANEL_SX}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <InsightsIcon sx={ACCENT_SX} />
        <Typography fontWeight={700} sx={ACCENT_SX}>Simulação</Typography>
        {showData && (
          <Chip size="small" label={`${showData.audience.total} clientes`} sx={TOTAL_CHIP_SX} />
        )}
        {loading && <Skeleton variant="rounded" animation="wave" width={96} height={24} sx={{ borderRadius: 4 }} />}
      </Stack>
      {showError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <Typography variant="body2" color="text.secondary" mb={2}>
          {showData ? (
            <>
              Estimativa a partir dos últimos {showData.base.periodDays} dias da rede: ticket médio {money(showData.base.avgTicket)},{' '}
              {liters(showData.base.avgLiters)} litros por abastecimento ({showData.base.fillins} abastecimentos no período).
            </>
          ) : (
            <Skeleton animation="wave" width="80%" />
          )}
        </Typography>
      )}

      {!showError && hasMaxCost && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mode}
              onChange={(_e, value: SimulationMode | null) => value && setMode(value)}
            >
              <ToggleButton value="mix">Pelo mix da rede</ToggleButton>
              <ToggleButton value="max">Custo máximo</ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Pelo mix da rede: pondera o desconto pelos litros que a rede vendeu de cada combustível nos últimos 30 dias. Custo máximo: supõe que todo cliente que volta abastece um combustível com desconto — bom para planejar o orçamento.">
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
            </Tooltip>
          </Stack>
          <Stack spacing={0.25} mb={2}>
            {!loading
              ? explanationLines.map((line, i) => (
                <Stack key={i} direction="row" spacing={0.5} alignItems="flex-start">
                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', mt: '3px' }} />
                  <Typography variant="body2" color="text.secondary">{line}</Typography>
                </Stack>
              ))
              : Array.from({ length: explanationLines.length || 1 }, (_, i) => (
                <Skeleton key={i} animation="wave" width="70%" />
              ))}
          </Stack>
        </>
      )}

      <Divider sx={{ mb: 2 }} />

      {!showError && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Se voltarem</TableCell>
              <TableCell align="right">Clientes</TableCell>
              <TableCell align="right">Custo estimado</TableCell>
              <TableCell align="right">Receita estimada</TableCell>
              <TableCell align="right">Receita por R$ de desconto</TableCell>
              {hasBudget && <TableCell align="right">Orçamento acaba em</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {showData && activeScenarios
              ? activeScenarios.map((s) => (
                <TableRow key={s.returnRate} selected={Math.abs(s.returnRate - customRate) < 0.001}>
                  <TableCell>{pct(s.returnRate)}</TableCell>
                  <TableCell align="right">{s.returns}</TableCell>
                  <TableCell align="right">{money(s.estimatedCost)}</TableCell>
                  <TableCell align="right">{money(s.estimatedRevenue)}</TableCell>
                  <TableCell align="right">{s.revenuePerReal != null ? `${s.revenuePerReal.toFixed(1)}x` : '—'}</TableCell>
                  {hasBudget && (
                    <TableCell align="right">
                      {s.budgetExhaustedAtReturns != null ? `${s.budgetExhaustedAtReturns} voltas` : '—'}
                    </TableCell>
                  )}
                </TableRow>
              ))
              : Array.from({ length: skeletonRows }, (_, row) => (
                <TableRow key={row}>
                  <TableCell><Skeleton animation="wave" width={36} /></TableCell>
                  {Array.from({ length: valueColumns }, (__, col) => (
                    <TableCell key={col} align="right">
                      <Skeleton animation="wave" width={72} sx={{ ml: 'auto' }} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}

      <Stack direction="row" alignItems="center" spacing={1.5} mt={2}>
        <TrendingUpIcon fontSize="small" sx={ACCENT_SX} />
        <Typography variant="body2">Taxa livre:</Typography>
        <TextField
          type="number"
          size="small"
          value={rateText}
          onChange={(e) => handleRateChange(e.target.value)}
          // Saiu do campo com valor fora de 1–100 (ou vazio): volta pra última taxa válida.
          onBlur={() => setRateText(ratePercentText(customRate))}
          slotProps={{
            input: { endAdornment: <InputAdornment position="end">%</InputAdornment> },
            htmlInput: { min: 1, max: 100 },
          }}
          sx={{ width: 110 }}
        />
      </Stack>

      {showData && showData.audience.byDaysBucket.length > 0 && (
        <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap" useFlexGap rowGap={0.5}>
          {showData.audience.byDaysBucket.map((b) => (
            <Chip key={b.label} size="small" variant="outlined" label={`${b.label}: ${b.count}`} />
          ))}
        </Stack>
      )}
      {loading && skeletonBuckets > 0 && (
        <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap" useFlexGap rowGap={0.5}>
          {Array.from({ length: skeletonBuckets }, (_, i) => (
            <Skeleton key={i} variant="rounded" animation="wave" width={128} height={24} sx={{ borderRadius: 4 }} />
          ))}
        </Stack>
      )}

      <Typography variant="caption" color="text.disabled" display="block" mt={1.5}>
        Estimativa: supõe que todo cliente que volta usa o cupom. O custo real depende de quantos usam de fato.
      </Typography>
    </Paper>
  );
};

export default SimulationPanel;
